import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp, Headphones, Book } from 'lucide-react';
import {
  fetchChapterByNumber,
  fetchSlokasByChapter,
  fetchCommentariesBySloka,
  getErrorMessage
} from '../api/client';
import WordBreakdown from './WordBreakdown';
import SlokaArtImage from './SlokaArtImage';
import ErrorMessage from './ErrorMessage';
import { useReadingProgress } from '../hooks/useReadingProgress';
import { DEFAULT_LANGUAGE } from '../utils/defaultLanguage';
import { useBookmarks } from '../hooks/useBookmarks';
import { useContinuousPlayer } from '../context/ContinuousPlayerContext';
import { Bookmark } from 'lucide-react';

const ReadMode = () => {
  const { number } = useParams();
  const navigate = useNavigate();
  const { startPlayback } = useContinuousPlayer();
  const { markRead } = useReadingProgress();
  const { isBookmarked, toggleBookmark } = useBookmarks();

  const [chapter, setChapter] = useState(null);
  const [slokas, setSlokas] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentary, setCommentary] = useState(null);
  const [commentaryOpen, setCommentaryOpen] = useState(false);
  const [loadingCommentary, setLoadingCommentary] = useState(false);

  const swipeStartX = useRef(null);

  const loadData = useCallback(async () => {
    if (!number) return;
    setLoading(true);
    setError(null);
    try {
      const ch = await fetchChapterByNumber(DEFAULT_LANGUAGE, parseInt(number, 10));
      setChapter(ch);
      const sl = await fetchSlokasByChapter(ch.language, ch.chapterNumber);
      setSlokas(sl.sort((a, b) => a.slokaNumber - b.slokaNumber));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [number]);

  useEffect(() => { loadData(); }, [loadData]);

  const sloka = slokas[currentIndex] || null;

  // Mark verse read + reset commentary when index changes
  useEffect(() => {
    if (sloka) {
      markRead(sloka.chapterNumber, sloka.slokaNumber);
      setCommentary(null);
      setCommentaryOpen(false);
    }
  }, [sloka, markRead]);

  const goTo = useCallback((idx) => {
    if (idx >= 0 && idx < slokas.length) setCurrentIndex(idx);
  }, [slokas.length]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') goTo(currentIndex + 1);
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') goTo(currentIndex - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentIndex, goTo]);

  // Touch swipe
  const onPointerDown = (e) => { swipeStartX.current = e.clientX; };
  const onPointerUp = (e) => {
    if (swipeStartX.current === null) return;
    const dx = e.clientX - swipeStartX.current;
    swipeStartX.current = null;
    if (dx < -60) goTo(currentIndex + 1);
    if (dx > 60) goTo(currentIndex - 1);
  };

  const loadCommentary = async () => {
    if (!sloka || commentary !== null) { setCommentaryOpen((v) => !v); return; }
    setLoadingCommentary(true);
    try {
      const data = await fetchCommentariesBySloka(sloka.language, sloka.chapterNumber, sloka.slokaNumber);
      setCommentary(data);
      setCommentaryOpen(true);
    } catch {
      setCommentary([]);
      setCommentaryOpen(true);
    } finally {
      setLoadingCommentary(false);
    }
  };

  if (loading) {
    return (
      <div className="read-mode-shell">
        <div className="read-mode-header">
          <div className="skeleton h-6 w-32 rounded" />
        </div>
        <div className="read-mode-body flex flex-col gap-4 p-6">
          <div className="skeleton h-64 w-full rounded-2xl" />
          <div className="skeleton h-5 w-2/3" />
          <div className="skeleton h-32 w-full rounded-xl" />
          <div className="skeleton h-20 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) return <div className="p-6"><ErrorMessage message={error} onRetry={loadData} /></div>;

  if (!chapter || !sloka) return null;

  const progress = slokas.length > 0 ? ((currentIndex + 1) / slokas.length) * 100 : 0;

  return (
    <div className="read-mode-shell">
      {/* Fixed header */}
      <header className="read-mode-header">
        <Link to={`/chapter/${chapter?.chapterNumber || number}`} className="read-mode-back">
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Ch {chapter.chapterNumber}: {chapter.chapterName}</span>
          <span className="sm:hidden">Back</span>
        </Link>
        <div className="read-mode-header-center">
          <Book className="w-4 h-4 text-gita-saffron" />
          <span className="text-sm font-medium text-gray-700">Reading Mode</span>
        </div>
        <button
          type="button"
          onClick={() => {
            startPlayback({ startChapter: chapter.chapterNumber, endChapter: chapter.chapterNumber, variant: 'apple' });
            navigate('/listen');
          }}
          className="read-mode-listen-btn"
        >
          <Headphones className="w-4 h-4" />
          <span className="hidden sm:inline">Listen</span>
        </button>
      </header>

      {/* Progress bar */}
      <div className="read-mode-progress-track">
        <div className="read-mode-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Swipeable verse area */}
      <main
        className="read-mode-body"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        style={{ touchAction: 'pan-y' }}
      >
        <div className="read-mode-verse-card">
          {/* Art */}
          <div className="read-mode-art-wrap">
            <SlokaArtImage
              chapterNumber={sloka.chapterNumber}
              slokaNumber={sloka.slokaNumber}
              fallback="chapter"
              alt=""
              className="read-mode-art"
            />
            <div className="read-mode-verse-badge">
              {chapter.chapterNumber}.{sloka.slokaNumber}
            </div>
            <button
              type="button"
              onClick={() => toggleBookmark({
                id: sloka._id,
                chapterNumber: sloka.chapterNumber,
                slokaNumber: sloka.slokaNumber,
                sloka: sloka.sloka,
                meaning: sloka.meaning
              })}
              className="read-mode-bookmark-btn"
              aria-label={isBookmarked(sloka._id) ? 'Remove bookmark' : 'Bookmark'}
            >
              <Bookmark className={`w-5 h-5 ${isBookmarked(sloka._id) ? 'fill-current text-gita-gold' : 'text-white/70'}`} />
            </button>
          </div>

          {/* Speaker */}
          {sloka.speaker && (
            <p className="text-xs text-gita-saffron font-medium tracking-wide uppercase mb-3">{sloka.speaker}</p>
          )}

          {/* Sloka text */}
          <div className="read-mode-sloka-text">
            {sloka.sloka}
          </div>

          {/* Word-by-word breakdown */}
          <WordBreakdown
            chapterNumber={sloka.chapterNumber}
            slokaNumber={sloka.slokaNumber}
            language={sloka.language || DEFAULT_LANGUAGE}
          />

          {/* Meaning */}
          <div className="read-mode-meaning">
            <p className="text-xs uppercase tracking-widest text-gita-muted font-semibold mb-2">Meaning</p>
            <p className="text-gray-700 leading-relaxed">{sloka.meaning}</p>
          </div>

          {/* Commentary toggle */}
          <button
            type="button"
            onClick={loadCommentary}
            disabled={loadingCommentary}
            className="read-mode-commentary-toggle"
          >
            <span>{commentaryOpen ? 'Hide commentary' : 'Show commentary'}</span>
            {commentaryOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {commentaryOpen && commentary && (
            <div className="read-mode-commentary">
              {commentary.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No commentary available for this verse.</p>
              ) : (
                commentary.map((c) => (
                  <div key={c._id} className="mb-4">
                    {c.author && <p className="text-sm font-semibold text-gita-maroon mb-1">— {c.author}</p>}
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{c.commentary}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </main>

      {/* Fixed footer nav */}
      <footer className="read-mode-footer">
        <button
          type="button"
          onClick={() => goTo(currentIndex - 1)}
          disabled={currentIndex === 0}
          className="read-mode-nav-btn"
          aria-label="Previous verse"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <span className="read-mode-counter">
          {currentIndex + 1} <span className="text-gray-400">/</span> {slokas.length}
        </span>
        <button
          type="button"
          onClick={() => goTo(currentIndex + 1)}
          disabled={currentIndex === slokas.length - 1}
          className="read-mode-nav-btn"
          aria-label="Next verse"
        >
          <ArrowRight className="w-5 h-5" />
        </button>
      </footer>
    </div>
  );
};

export default ReadMode;
